import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Download, X } from 'lucide-react'
import {
  fetchAntivirusDevices,
  fetchOnlineDevices,
  fetchOsDevices,
} from '@/api/charts'
import { fetchBackupHosts } from '@/api/backup'
import { fetchNasHosts } from '@/api/nas'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { shortenOsLabel } from '@/lib/chartLabels'
import { exportChartSliceDrillDownCsv } from '@/lib/exportDashboardDrillDown'
import { cn } from '@/lib/utils'

/** ~10 body rows (+ header) before vertical scroll. */
const TABLE_SCROLL_CLASS = 'max-h-[32.5rem] overflow-y-auto'

const BACKUP_STATUS_MAP = {
  OK: 'PASS',
  Review: 'WARN',
  'Action needed': 'FAIL',
  'No data': 'NONE',
}

const ANTIVIRUS_BUCKET_MAP = {
  'Virus detected': 'virus_detected',
  'No AV': 'no_av',
  'AV not updated': 'av_not_updated',
  OK: 'ok',
  'No data': 'no_data',
}

function backupBucket(host) {
  const status = String(host?.overall_status || '').toUpperCase()
  if (status === 'PASS') return 'PASS'
  if (status === 'WARN') return 'WARN'
  if (status === 'FAIL') return 'FAIL'
  return 'NONE'
}

function DeviceTable({ items, showCustomer, detailHeader, renderDetail, linkFor }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className={TABLE_SCROLL_CLASS}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-border bg-muted text-left text-muted-foreground">
              <th className="px-4 py-3 font-medium">Device</th>
              {showCustomer ? (
                <th className="px-4 py-3 font-medium">Customer</th>
              ) : null}
              <th className="px-4 py-3 font-medium">{detailHeader}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const key = item.agent_id ?? `${item.source}-${item.hostname}`
              const href = linkFor?.(item)
              const name =
                item.hostname || item.display_name || (item.agent_id ? `Device ${item.agent_id}` : '—')
              return (
                <tr key={key} className="border-b border-border/60 last:border-0 align-top">
                  <td className="px-4 py-3">
                    {href ? (
                      <Link
                        to={href}
                        className="font-medium text-primary hover:underline"
                      >
                        {name}
                      </Link>
                    ) : (
                      <span className="font-medium">{name}</span>
                    )}
                    {item.display_name && item.hostname ? (
                      <p className="text-xs text-muted-foreground">{item.display_name}</p>
                    ) : null}
                  </td>
                  {showCustomer ? (
                    <td className="px-4 py-3 text-muted-foreground">
                      {item.customer_name || '—'}
                    </td>
                  ) : null}
                  <td className="px-4 py-3 text-muted-foreground">{renderDetail(item)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Pagination({ page, pages, loading, onPageChange }) {
  if (pages <= 1) return null
  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">
        Page {page} of {pages}
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1 || loading}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= pages || loading}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

export function ChartSliceDrillDown({
  filter,
  customerIds,
  showCustomer = false,
  onClose,
}) {
  const [page, setPage] = useState(1)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState('')
  const filterKey = filter ? `${filter.chart}:${filter.key}` : ''
  const customerKey = Array.isArray(customerIds) ? customerIds.join(',') : ''

  useEffect(() => {
    setPage(1)
    setResult(null)
    setExportError('')
  }, [filterKey, customerKey])

  useEffect(() => {
    if (!filter?.chart || !filter?.key) {
      setResult(null)
      return undefined
    }

    let cancelled = false
    setLoading(true)

    async function load() {
      try {
        if (filter.chart === 'online') {
          const online = filter.key === 'Online'
          const data = await fetchOnlineDevices(customerIds, online, page, 50)
          if (!cancelled) setResult(data)
          return
        }
        if (filter.chart === 'os') {
          const data = await fetchOsDevices(customerIds, filter.key, page, 50)
          if (!cancelled) setResult(data)
          return
        }
        if (filter.chart === 'antivirus') {
          const bucket =
            filter.bucket || ANTIVIRUS_BUCKET_MAP[filter.key] || filter.key
          const data = await fetchAntivirusDevices(
            customerIds,
            bucket,
            page,
            50,
          )
          if (!cancelled) setResult(data)
          return
        }
        if (filter.chart === 'backup') {
          const bucket = BACKUP_STATUS_MAP[filter.key]
          const [server, nas] = await Promise.all([
            fetchBackupHosts(customerIds).catch(() => ({ hosts: [] })),
            fetchNasHosts(customerIds).catch(() => ({ hosts: [] })),
          ])
          const combined = [
            ...(server.hosts || []).map((host) => ({
              ...host,
              source: 'server',
            })),
            ...(nas.hosts || []).map((host) => ({
              ...host,
              source: 'nas',
            })),
          ]
          const matched = combined
            .filter((host) => backupBucket(host) === bucket)
            .sort((a, b) =>
              String(a.hostname || '').localeCompare(String(b.hostname || ''), 'en', {
                sensitivity: 'base',
              }),
            )
          const pageSize = 50
          const total = matched.length
          const pages = Math.max(1, Math.ceil(total / pageSize) || 1)
          const start = (page - 1) * pageSize
          if (!cancelled) {
            setResult({
              items: matched.slice(start, start + pageSize),
              total,
              page,
              pages,
              page_size: pageSize,
            })
          }
          return
        }
        if (!cancelled) setResult(null)
      } catch {
        if (!cancelled) setResult(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [filter, customerIds, page])

  if (!filter?.chart || !filter?.key) return null

  const title =
    filter.chart === 'online'
      ? `Online / offline — ${filter.key}`
      : filter.chart === 'os'
        ? `OS — ${shortenOsLabel(filter.key)}`
        : filter.chart === 'antivirus'
          ? `Antivirus — ${filter.key}`
          : `Server/NAS Backup — ${filter.key}`

  const noun = filter.chart === 'backup' ? 'host' : 'device'
  const detailHeader =
    filter.chart === 'online'
      ? 'Status'
      : filter.chart === 'os'
        ? 'Operating system'
        : filter.chart === 'antivirus'
          ? 'Issue'
          : 'Detail'

  async function handleExport() {
    setExporting(true)
    setExportError('')
    try {
      await exportChartSliceDrillDownCsv({
        filter,
        customerIds,
        showCustomer,
      })
    } catch {
      setExportError('Could not export this list.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle className="text-base">{title}</CardTitle>
          <p className="text-xs text-muted-foreground">
            {loading
              ? `Loading ${noun}s…`
              : result
                ? `${result.total} ${noun}${result.total === 1 ? '' : 's'} in this slice`
                : `Could not load ${noun}s.`}
          </p>
          {exportError ? (
            <p className="text-xs text-destructive">{exportError}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting || loading || !result?.total}
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close list">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !result ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !result?.items?.length ? (
          <p className="text-sm text-muted-foreground">No {noun}s in this slice.</p>
        ) : (
          <>
            <DeviceTable
              items={result.items}
              showCustomer={showCustomer}
              detailHeader={detailHeader}
              linkFor={(item) => {
                if (item.agent_id) {
                  return filter.chart === 'antivirus'
                    ? `/agents/${item.agent_id}?tab=security`
                    : `/agents/${item.agent_id}`
                }
                if (item.source === 'nas') return '/nas'
                if (item.source === 'server') return '/backup'
                return null
              }}
              renderDetail={(item) => {
                if (filter.chart === 'online') {
                  return item.online_status ? 'Online' : 'Offline'
                }
                if (filter.chart === 'os') {
                  return item.os_name || 'Unknown'
                }
                if (filter.chart === 'antivirus') {
                  const issue =
                    (item.issues && item.issues[0]) ||
                    item.label ||
                    filter.key
                  const cover = item.cover_label
                    ? `Cover: ${item.cover_label}`
                    : item.av_product
                      ? `AV: ${item.av_product}`
                      : null
                  return (
                    <span className={cn('block')}>
                      {issue}
                      {cover ? (
                        <span className="mt-1 block text-xs">{cover}</span>
                      ) : null}
                    </span>
                  )
                }
                const bits = [
                  item.source === 'nas' ? 'NAS' : 'Server',
                  item.overall_status || 'No data',
                  item.interpretation,
                ].filter(Boolean)
                return (
                  <span className={cn('block')}>
                    {bits[0]}
                    {bits[1] ? ` · ${bits[1]}` : ''}
                    {bits[2] ? (
                      <span className="mt-1 block text-xs">{bits[2]}</span>
                    ) : null}
                  </span>
                )
              }}
            />
            <Pagination
              page={result.page}
              pages={result.pages}
              loading={loading}
              onPageChange={setPage}
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}
